/**
 * Hash Utilities for Content-Based Deduplication
 * Used to prevent duplicate photo uploads
 */

/**
 * Compute MD5 hash of a Blob/File
 * Returns first 12 characters of hex hash (same as backend)
 */
export const computeFileHash = async (file: Blob): Promise<string> => {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  // Return first 12 chars to match backend MD5 truncation length
  return hashHex.substring(0, 12);
};

/**
 * Recent upload hash storage for native apps
 * Uses localStorage to persist across app restarts
 */
const HASH_STORAGE_KEY = 'recent_upload_hashes';
const HASH_EXPIRY_MS = 60 * 60 * 1000; // 1 hour

interface StoredHash {
  hash: string;
  jobId: number;
  timestamp: number;
  type: string;
}

/**
 * Get all stored hashes (cleaning up expired ones)
 */
export const getStoredHashes = (): StoredHash[] => {
  try {
    const stored = localStorage.getItem(HASH_STORAGE_KEY);
    if (!stored) return [];

    const hashes: StoredHash[] = JSON.parse(stored);
    const now = Date.now();

    // Filter out expired hashes
    const valid = hashes.filter(h => now - h.timestamp < HASH_EXPIRY_MS);

    // Save cleaned up list if we removed any
    if (valid.length !== hashes.length) {
      localStorage.setItem(HASH_STORAGE_KEY, JSON.stringify(valid));
    }

    return valid;
  } catch {
    return [];
  }
};

/**
 * Check if a hash already exists for a given job and type
 */
export const isDuplicateHash = (hash: string, jobId: number, type: string): boolean => {
  const hashes = getStoredHashes();
  return hashes.some(h => h.hash === hash && h.jobId === jobId && h.type === type);
};

/**
 * Store a hash for a recent upload
 */
export const storeUploadHash = (hash: string, jobId: number, type: string): void => {
  try {
    const hashes = getStoredHashes();

    // Don't add if already exists
    if (hashes.some(h => h.hash === hash && h.jobId === jobId && h.type === type)) {
      return;
    }

    hashes.push({
      hash,
      jobId,
      timestamp: Date.now(),
      type
    });

    localStorage.setItem(HASH_STORAGE_KEY, JSON.stringify(hashes));
  } catch (err) {
    console.error('[HashUtils] Failed to store hash:', err);
  }
};

/**
 * Remove a specific hash (if upload fails and we want to allow retry)
 */
export const removeUploadHash = (hash: string, jobId: number, type: string): void => {
  try {
    const hashes = getStoredHashes();
    const filtered = hashes.filter(h => !(h.hash === hash && h.jobId === jobId && h.type === type));
    localStorage.setItem(HASH_STORAGE_KEY, JSON.stringify(filtered));
  } catch (err) {
    console.error('[HashUtils] Failed to remove hash:', err);
  }
};

/**
 * Clear all stored hashes (for testing/debugging)
 */
export const clearStoredHashes = (): void => {
  localStorage.removeItem(HASH_STORAGE_KEY);
};
